import * as Icon from 'react-feather';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  DataObjectGroupProcessingStateType,
  DataObjectType,
  DropdownItem,
  GetApiErrorMessage,
  InputDataActiveInfoParams,
  useInputDataActionMutation,
  useAlertContext,
  getGroupDescriptionRef,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { Button, Dropdown, Flex } from 'antd';
import { MenuInfo } from 'rc-menu/lib/interface';
import { FC, useMemo, memo } from 'react';
import styled from 'styled-components';
import translations from './InputDataActionCell.translations.json';
import {
  getRowIdAndType,
  useNavigateToRelationsWithLatestVersion,
} from './utils';
import { useInputDataStore } from './InputDataContext';
import { CellContext } from '@tanstack/react-table';
import { path } from '../../paths';
import UriTemplate from 'uri-template-lite';
import { RelationsTablesModalParams } from '../relations/RelationsTablesModal';

const { addTranslations, t, useTranslation, exists } = i18nNsFactory(
  'input-data-action-cell',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export const InputDataActionCell: FC<
  CellContext<DataObjectGroupProcessingStateType, any> & {
    onShowActiveInputInfo: (params: InputDataActiveInfoParams) => void;
    onActionSuccess?: () => unknown;
    onShowRelationsTables?: (params: RelationsTablesModalParams) => void;
    onDropdownOpenChange?: (open: boolean) => void;
  }
> = memo((props) => {
  const { t } = useTranslation();

  const { setError } = useAlertContext();
  const { mutateAsync } = useInputDataActionMutation({
    throwOnError: (error) => {
      return error.status !== 403;
    },
  });
  const [, setLoadingError] = useInputDataStore('loadingError');
  const processingState = props.row.original;
  const {
    onShowActiveInputInfo,
    onActionSuccess,
    onShowRelationsTables,
    onDropdownOpenChange,
  } = props;

  const { id, name, type, valueDate } = useMemo(
    () => getRowIdAndType(processingState),
    [processingState],
  );
  const {
    confidential,
    hasAccess,
    dataObjectGroupId,
    standaloneDataObjectDescriptionId,
  } = processingState;

  const isAccessible = !confidential || hasAccess;
  const isRunning =
    processingState.processing?.phaseCode === 'PENDING' ||
    processingState.processing?.phaseCode === 'ACTIVE';

  const navigateToRelationsWithLatestVersion =
    useNavigateToRelationsWithLatestVersion(
      'INPUT',
      valueDate,
      dataObjectGroupId,
      standaloneDataObjectDescriptionId,
    );

  const actionMenu = useMemo(
    () => ({
      items: [
        {
          label: <DropdownItem>{t('menu.sample')}</DropdownItem>,
          key: 'sample',
        },
        {
          label: <DropdownItem>{t('menu.impute')}</DropdownItem>,
          key: 'impute',
        },
        {
          label: <DropdownItem>{t('menu.incremental')}</DropdownItem>,
          key: 'loadDataIncremental',
        },
        {
          label: <DropdownItem>{t('menu.all')}</DropdownItem>,
          key: 'loadData',
        },
      ],
      onClick: (e: MenuInfo) => {
        if (name) {
          mutateAsync({
            data: {
              action: e.key,
              code: name.split(' - ')[0],
              valueDate,
            },
          })
            .then(() => {
              onActionSuccess?.();
            })
            .catch((error) => {
              if (error.status === 403) {
                setError(translateMutationError('forbidden'));
                return;
              }
              const apiError = (error.response?.data.errors ?? [])[0];
              if (apiError) {
                setLoadingError(
                  translateMutationError(apiError.code, apiError.meta),
                );
              }
            });
        }
      },
    }),
    [
      mutateAsync,
      name,
      onActionSuccess,
      setError,
      setLoadingError,
      t,
      valueDate,
    ],
  );

  const infoMenu = useMemo(
    () => ({
      items: [
        ...(isAccessible
          ? [
              {
                label: <DropdownItem>{t('menu.inputData')}</DropdownItem>,
                key: 'inputData',
                onClick: () => {
                  const url = new UriTemplate(
                    `${path.inputDataView}{?date,type}`.replace(':id', '{id}'),
                  ).expand({
                    id,
                    type,
                    date: valueDate,
                  });
                  window.open(url, '_blank');
                },
              },
            ]
          : []),
        {
          label: <DropdownItem>{t('menu.relations')}</DropdownItem>,
          key: 'relations',
          onClick: () => navigateToRelationsWithLatestVersion(),
        },
        {
          label: <DropdownItem>{t('menu.relationsTables')}</DropdownItem>,
          key: 'relationsTables',
          onClick: () => {
            onShowRelationsTables?.({
              groupOrDescriptionId: getGroupDescriptionRef(
                dataObjectGroupId,
                standaloneDataObjectDescriptionId,
              ),
              valueDate,
              version: 'latest',
              dataObjectType: DataObjectType.IN,
              isGroup: !!standaloneDataObjectDescriptionId ? false : true,
              rootNodeName: name || '',
            });
          },
        },
        {
          label: <DropdownItem>{t('menu.active')}</DropdownItem>,
          key: 'active',
          onClick: () => {
            if (!dataObjectGroupId) return;
            onShowActiveInputInfo({ groupId: dataObjectGroupId, valueDate });
          },
        },
      ],
    }),
    [
      isAccessible,
      t,
      id,
      type,
      valueDate,
      navigateToRelationsWithLatestVersion,
      onShowRelationsTables,
      dataObjectGroupId,
      standaloneDataObjectDescriptionId,
      name,
      onShowActiveInputInfo,
    ],
  );

  return (
    <Flex gap="small">
      <Dropdown
        menu={actionMenu}
        trigger={['click']}
        onOpenChange={onDropdownOpenChange}
      >
        <StyledButton
          id={`input-action-${type}-${id}`}
          size="small"
          disabled={isRunning}
        >
          {t('button.execute')}
          <Icon.ChevronDown size={18} />
        </StyledButton>
      </Dropdown>
      <Flex style={{ marginLeft: 'auto' }}>
        <Dropdown
          menu={infoMenu}
          trigger={['click']}
          placement="bottomRight"
          onOpenChange={onDropdownOpenChange}
        >
          <Button
            id={`input-info-${type}-${id}`}
            type="text"
            shape="circle"
            icon={<Icon.MoreVertical size={20} />}
            size="small"
          />
        </Dropdown>
      </Flex>
    </Flex>
  );
});

const StyledButton = styled(Button)`
  margin-block: -1px;
  width: 90px;
`;
